/**
 * NoteReplies — the reply thread that hangs under any unified note.
 *
 * One component, dropped into every notes surface (deals, faults, service
 * requests, jobs, scheduler, billing, customers, tech app) so a reply looks
 * and behaves identically wherever the note is read. It owns:
 *   - the collapsed "N replies" affordance
 *   - the reply bubbles
 *   - an inline composer with the same @tag picker as the main composer
 *   - the write, via createNoteReply (which handles thread notifications)
 *
 * Deliberately unaware of WHICH entity it sits on — a reply inherits its
 * parent's entity, so the caller only has to hand over the parent note.
 */

import { useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AtSign, CornerDownRight, Loader2, MessageSquare, Send, Trash2 } from 'lucide-react';
import type { Note, NoteSource } from '@/types';
import type { MentionableUser } from '@/types/mention.types';
import { createNoteReply, deleteNote, threadParticipants } from '@/services/notes.service';
import { useSession } from '@/context/AuthSessionContext';
import {
  applyMention,
  findMentionTrigger,
  matchesMentionQuery,
  mentionHandleFor,
} from '@/utils/mention-trigger';
import { getLocaleSync } from '@/utils/locale';

/**
 * Which palette to render in. The app runs three side by side:
 *   - `theme` — themed surface tokens (deal / fault / SR / billing screens)
 *   - `dark`  — fixed dark-glass classes (scheduler side panel, customer modal)
 *   - `light` — the tech app's fixed light card stack, which uses literal
 *               gray/white classes and does NOT follow the theme tokens, so a
 *               themed reply would render white-on-white there in dark mode.
 */
export type NoteTone = 'theme' | 'dark' | 'light';

interface NoteRepliesProps {
  /** The note being replied to — must carry an id. */
  parent: Note;
  /** Replies for this note, oldest first (see buildNoteThreads). */
  replies: Note[];
  availableUsers: MentionableUser[];
  /** Used in the mention notification headline, e.g. "Deal · Jane Smith". */
  entityTitle?: string;
  /** Audit-trail source for the reply note. Defaults to the parent's. */
  source?: NoteSource;
  tone?: NoteTone;
  /** Collapse the thread behind a toggle once it passes this many replies. */
  collapseAfter?: number;
}

const TONE = {
  theme: {
    rule: 'border-theme-soft',
    muted: 'text-theme-muted',
    tertiary: 'text-theme-tertiary',
    primary: 'text-theme-primary',
    secondary: 'text-theme-secondary',
    bubble: 'bg-surface-hover/50 border-theme-soft',
    input: 'bg-surface-input border-theme focus:border-sky-500/50',
    menu: 'bg-surface-card border-theme',
    menuItem: 'text-theme-primary hover:bg-surface-hover',
    action: 'text-theme-muted hover:text-sky-400',
    send: 'bg-sky-500 hover:bg-sky-600 text-white',
    mention: 'text-sky-500 dark:text-sky-400 font-medium',
  },
  light: {
    rule: 'border-gray-200',
    muted: 'text-gray-400',
    tertiary: 'text-gray-500',
    primary: 'text-gray-800',
    secondary: 'text-gray-700',
    bubble: 'bg-gray-50 border-gray-200',
    input: 'bg-white border-gray-300 focus:border-amber-400',
    menu: 'bg-white border-gray-200',
    menuItem: 'text-gray-800 hover:bg-gray-50',
    action: 'text-gray-500 hover:text-amber-600',
    send: 'bg-amber-500 hover:bg-amber-600 text-white',
    mention: 'text-amber-600 font-semibold',
  },
  dark: {
    rule: 'border-white/10',
    muted: 'text-white/30',
    tertiary: 'text-white/40',
    primary: 'text-white/90',
    secondary: 'text-white/70',
    bubble: 'bg-white/[0.03] border-white/10',
    input: 'bg-white/5 border-white/10 focus:border-amber-500/40',
    menu: 'bg-slate-900 border-white/10',
    menuItem: 'text-white/80 hover:bg-white/5',
    action: 'text-white/30 hover:text-amber-300',
    send: 'bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30',
    mention: 'text-amber-300/90 font-semibold',
  },
} as const;

export function NoteReplies({
  parent,
  replies,
  availableUsers,
  entityTitle,
  source,
  tone = 'theme',
  collapseAfter = 3,
}: NoteRepliesProps) {
  const t = TONE[tone];

  const [composing, setComposing] = useState(false);
  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Identity via the session store, not raw localStorage — Wave 2 of the
  // overhaul owns those keys (see src/lib/session.ts).
  const session = useSession();
  const currentUserId = session.userId ?? session.techId ?? '';
  const currentUserName = session.displayName ?? session.techName ?? 'Unknown';

  const matches = useMemo(
    () => availableUsers.filter((u) => matchesMentionQuery(u, pickerQuery)).slice(0, 6),
    [availableUsers, pickerQuery],
  );

  // Long threads collapse to the last few so a chatty note doesn't bury the
  // rest of the timeline. The newest replies are the ones worth showing.
  const overflow = Math.max(0, replies.length - collapseAfter);
  const visible = expanded || overflow === 0 ? replies : replies.slice(-collapseAfter);

  const syncPicker = (text: string) => {
    const caret = textareaRef.current?.selectionStart ?? text.length;
    const trigger = findMentionTrigger(text, caret);
    if (!trigger) {
      setPickerOpen(false);
      return;
    }
    setPickerQuery(trigger.query);
    setPickerOpen(true);
  };

  const handleChange = (text: string) => {
    setContent(text);
    syncPicker(text);
  };

  const pickMention = (user: MentionableUser) => {
    const el = textareaRef.current;
    const caret = el?.selectionStart ?? content.length;
    const trigger = findMentionTrigger(content, caret);
    if (!trigger) return;

    const next = applyMention(
      content,
      caret,
      trigger.atIndex,
      mentionHandleFor(user, availableUsers),
    );
    setContent(next.text);
    setPickerOpen(false);

    setTimeout(() => {
      el?.focus();
      el?.setSelectionRange(next.cursor, next.cursor);
    }, 0);
  };

  const submit = async () => {
    const trimmed = content.trim();
    if (!trimmed || submitting || !parent.id) return;
    setSubmitting(true);
    try {
      await createNoteReply({
        parent,
        content: trimmed,
        createdBy: currentUserId,
        createdByName: currentUserName,
        source: source ?? parent.source,
        entityTitle,
        availableUsers,
        threadParticipants: threadParticipants(parent, replies),
      });
      setContent('');
      setComposing(false);
      setExpanded(true);
    } catch (err) {
      console.error('[NoteReplies] reply failed:', err);
      alert('Failed to post reply. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // A note that was never saved can't be replied to.
  if (!parent.id) return null;

  return (
    <div className={`mt-2 pt-2 border-t ${t.rule}`}>
      {overflow > 0 && !expanded && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className={`text-[11px] font-medium mb-1.5 ${t.tertiary} hover:underline`}
        >
          Show {overflow} earlier {overflow === 1 ? 'reply' : 'replies'}
        </button>
      )}

      {visible.length > 0 && (
        <div className={`space-y-1.5 mb-1.5 pl-2.5 border-l-2 ${t.rule}`}>
          <AnimatePresence initial={false}>
            {visible.map((reply) => (
              <motion.div
                key={reply.id}
                layout
                data-note-id={reply.id}
                initial={{ opacity: 0, y: -3 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.15 }}
                className={`rounded-lg border px-2.5 py-1.5 ${t.bubble}`}
              >
                <div className="flex items-center gap-1.5">
                  <span className={`text-[11px] font-semibold ${t.primary}`}>
                    {reply.createdByName}
                  </span>
                  <span className={`text-[10px] ${t.muted}`}>
                    {formatReplyTime(reply.createdAt)}
                  </span>
                  {reply.createdBy === currentUserId && (
                    <button
                      type="button"
                      onClick={() => {
                        if (!reply.id) return;
                        if (!confirm('Delete this reply?')) return;
                        void deleteNote(reply.id, currentUserId, parent.id);
                      }}
                      className={`ml-auto p-0.5 rounded transition-colors ${t.action}`}
                      title="Delete reply"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
                <p
                  className={`text-[13px] leading-relaxed whitespace-pre-wrap break-words ${t.secondary}`}
                >
                  {highlightMentions(reply.content, t.mention)}
                </p>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      {!composing ? (
        <button
          type="button"
          onClick={() => {
            setComposing(true);
            setTimeout(() => textareaRef.current?.focus(), 0);
          }}
          className={`inline-flex items-center gap-1.5 text-[11px] font-medium transition-colors ${t.action}`}
        >
          {replies.length > 0 ? (
            <MessageSquare className="w-3 h-3" />
          ) : (
            <CornerDownRight className="w-3 h-3" />
          )}
          {replies.length > 0
            ? `Reply · ${replies.length}`
            : 'Reply'}
        </button>
      ) : (
        <div className="relative">
          <textarea
            ref={textareaRef}
            value={content}
            onChange={(e) => handleChange(e.target.value)}
            onSelect={(e) => syncPicker(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                if (pickerOpen) {
                  setPickerOpen(false);
                } else {
                  setComposing(false);
                  setContent('');
                }
                return;
              }
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                void submit();
              }
            }}
            placeholder="Reply… use @ to tag someone"
            rows={2}
            disabled={submitting}
            className={`w-full rounded-lg border px-2.5 py-1.5 text-[13px] resize-none focus:outline-none ${t.input} ${t.primary}`}
          />

          {pickerOpen && matches.length > 0 && (
            <div
              className={`absolute left-0 right-0 top-full mt-1 z-30 border rounded-lg shadow-xl max-h-44 overflow-y-auto ${t.menu}`}
            >
              {matches.map((u) => (
                <button
                  key={u.id}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pickMention(u)}
                  className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 transition-colors ${t.menuItem}`}
                >
                  <AtSign className="w-3 h-3 opacity-70" />
                  <span>{u.name}</span>
                  {u.role && <span className={`ml-auto text-[10px] ${t.muted}`}>{u.role}</span>}
                </button>
              ))}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 mt-1.5">
            <button
              type="button"
              onClick={() => {
                setComposing(false);
                setContent('');
                setPickerOpen(false);
              }}
              className={`text-[11px] ${t.tertiary} hover:underline`}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void submit()}
              disabled={!content.trim() || submitting}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${t.send}`}
            >
              {submitting ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <Send className="w-3 h-3" />
              )}
              Reply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// HELPERS
// ============================================================================

/** Visual-only @handle tint — resolution happened at write time. */
function highlightMentions(text: string, className: string): React.ReactNode {
  return text.split(/(@[\w.-]+)/g).map((part, i) =>
    part.startsWith('@') ? (
      <span key={i} className={className}>
        {part}
      </span>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
}

function formatReplyTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';

  const diffMins = Math.floor((Date.now() - date.getTime()) / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;

  return date.toLocaleString(getLocaleSync(), {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
}
